import type { ReactNode } from 'react';
import type { AdvRecs } from '../../data/types.js';
import {
  ContextStrip,
  QuarantineNote,
  RecommendationCard,
  ResolvedAccepted,
  ResolvedModified,
  ResolvedRejected,
} from '../../components/advisor.js';

/* Garnet · AI Advisor — In-context recommendations.
   The inline card in its four states (pending / accepted / modified / rejected)
   plus the two provenance edge-cases: a recommendation grounded in an untrusted
   document, and a document that tried to instruct the model (quarantined). */

function StateLabel({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '0 0 11px' }}>
      <span
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
        }}
      >
        {children}
      </span>
      {hint && <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted-2)' }}>{hint}</span>}
    </div>
  );
}

export function Recs({ recs }: { recs: AdvRecs }) {
  return (
    <div className="adv-stage-inner">
      <div className="adv-h">
        <div className="eb">§ In-context recommendation · advisor</div>
        <h1>The inline card, and its states</h1>
        <p>
          The recommendation card appears in context — on a record, an intake, a case, the sourcing board. It carries the
          recommendation, a one-line rationale, its grounding, a confidence, and three actions. It never shows an
          assurance-state colour.
        </p>
      </div>

      {/* live, interactive card */}
      <StateLabel hint="interactive — try accept / modify / reject">pending · on a live record</StateLabel>
      <ContextStrip ctx={recs.pending.ctx} tier="high" />
      <div style={{ marginTop: 12 }}>
        <RecommendationCard rec={recs.pending} acceptLink="REQ-4471-3 raised" />
      </div>

      {/* the three resolved states */}
      <div className="adv-seclabel">Resolved states · the human responded</div>
      <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: '-4px 0 14px', maxWidth: '64ch', lineHeight: 1.5 }}>
        Once answered, the card drops the iris tint — the advice has become a human decision (or been declined), and is no
        longer advice. Each response names who acted and when, for the audit trail.
      </p>

      <StateLabel>accepted</StateLabel>
      <ResolvedAccepted rec={recs.accepted} />

      <div style={{ height: 18 }} />
      <StateLabel>modified</StateLabel>
      <ResolvedModified rec={recs.modified} />

      <div style={{ height: 18 }} />
      <StateLabel>rejected</StateLabel>
      <ResolvedRejected rec={recs.rejected} />

      {/* provenance edge-cases */}
      <div className="adv-seclabel">Provenance, made visible</div>

      <StateLabel hint="grounding source is not machine-trusted">grounded in an untrusted document</StateLabel>
      <ContextStrip ctx={recs.untrusted.ctx} tier="standard" />
      <div style={{ marginTop: 12 }}>
        <RecommendationCard rec={recs.untrusted} acceptLink="verification request raised" />
      </div>

      <div style={{ height: 22 }} />
      <StateLabel hint="this is a feature, not an error">a document that tried to instruct the model</StateLabel>
      <QuarantineNote q={recs.quarantine} />
      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginTop: 11, lineHeight: 1.6, maxWidth: '66ch' }}>
        The quarantined text never reached recommendation-forming. The Advisor surfaces it so a human can see the attempt —
        the document is flagged on the record and excluded from every citation.
      </div>
    </div>
  );
}
